import { useEffect, useRef, useState } from "react";
import Alert from "react-bootstrap/Alert";
import { useNavigate } from "react-router-dom";
import Spinner from "../../feature/Spinner";
import ProductSelector from "../../feature/shop/ProductSelector";
import Translated from "../../feature/Translated";
import Button from "react-bootstrap/Button";
import ContactForm from "../../feature/ContactForm";
import { WebShopTestimonialsMobile, WebShopTestimonials } from "../../feature/shop/WebShopTestimonials";
import Collapse from "react-bootstrap/Collapse";
import ActivityCount from "../../feature/ActivityCount";
import TrackerAB from "../../../utils/trackerAB";
import ImagesComponent from "../../feature/shop/ImagesComponent";
import { PRODUCT_PAGE_ID } from "./ProductPage";
import OtherProductCategories from "../../feature/shop/OtherShopCategories";
import ShopPaymentTermsNote from "../../feature/shop/ShopPaymentTermsNote";
import ShopCancellingTermsNote from "../../feature/shop/ShopCancellingTermsNote";
import usePendingOrderCheck from "../../../hooks/usePendingOrderCheck";
import { useParams } from "react-router-dom";
import { useTranslation } from "../../feature/TranslationProvider";
import { useGlobalData } from "../../../context/GlobalContext";
import { useProductsPage } from "../../../api-new";
import useOpenPrivacyPolicy from '../../../hooks/useOpenPrivacyPolicy';

const ProductsPage = () => {
  usePendingOrderCheck();
  const { productsPageId } = useParams();
  const { setPageData } = useGlobalData();
  const { lang } = useTranslation();
  const navigate = useNavigate();
  const handlePrivacyPolicyClick = useOpenPrivacyPolicy();
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactFormSubmitted, setContactFormSubmitted] = useState(false);
  const [showPaymentTypes, setShowPaymentTypes] = useState(true);
  const [showImagePromo, setShowImagePromo] = useState(false);
  const imagePromoTimeoutRef = useRef(null);
  const productsPageQuery = useProductsPage(productsPageId, lang);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    TrackerAB.track("webshop-products-page-shown");
    imagePromoTimeoutRef.current = setTimeout(
      () => setShowImagePromo(true),
      5000
    );

    return () => {
      clearTimeout(imagePromoTimeoutRef.current);
    };
  }, []);

  const selectProduct = (product) => {
    setPageData(PRODUCT_PAGE_ID, product);
    navigate("/tuoteinfo");
  };

  const renderInfo = (text, needsTranslation = false) => {
    if (!text) {
      return null;
    }

    if (needsTranslation) {
      return (
        <div className="blue-inner-box tight">
          <Translated translationKey={text} html={true} />
        </div>
      );
    }

    return (
      <div className="blue-inner-box tight">{text}</div>
    );
  };

  const renderContactForm = () => {
    const buttonKey = showContactForm ? "cancel" : "contact";
    let content;
    if (showContactForm) {
      content = (
        <div className="top-margin contact-wrapper white-box slide-in">
          <ContactForm
            category="product_inquiry"
            onSubmitted={() => {
              setContactFormSubmitted(true);
              setShowContactForm(false);
            }}
          />
        </div>
      );
    } else if (contactFormSubmitted) {
      content = <Translated translationKey="message_sent" />;
    }

    return (
      <div className="large-top-margin webshop-contact-wrapper">
        <Button
          aria-label="Send feedback"
          role="presentation"
          onClick={() => {
            setShowContactForm((prev) => !prev);
            setContactFormSubmitted(false);
          }}
        >
          <Translated translationKey={buttonKey} />
        </Button>
        {content}
      </div>
    );
  };

  const renderProducts = (products) => {
    return (
      <div className="medium-top-margin medium-bottom-margin">
        <ProductSelector products={products} onSelect={selectProduct} />
      </div>
    );
  };

  const renderChatBubblesMobile = () => {
    if (showContactForm) {
      return null;
    }

    return <WebShopTestimonialsMobile />;
  };

  const renderChatBubbles = () => {
    if (showContactForm) {
      return null;
    }

    return <WebShopTestimonials />;
  };

  const renderPaymentBanner = () => {
    if (showContactForm) {
      return null;
    }

    const banner = (
      <div className="semi-dark-inner-box tight fade-in delay top-margin-small">
        {showPaymentTypes ? null : (
          <div className="centered">
            <Button
              bsstyle="link"
              aria-label="Show payment types"
              role="presentation"
              onClick={() => {
                TrackerAB.track("webshop-show-payment-types");
                setShowPaymentTypes(true);
              }}
            >
              <Translated translationKey="payment_types" />
              ...
            </Button>
          </div>
        )}
        <Collapse in={showPaymentTypes}>
          <div className="top-margin">
            {
              <div className="centered fade-in">
                <Translated translationKey="payment_types" className="strong" />
                <br />
                <Translated translationKey="payment_operator_info" />
                <ShopPaymentTermsNote /> <ShopCancellingTermsNote />
              </div>
            }
          </div>
        </Collapse>
      </div>
    );

    return banner;
  };

  const renderPromoImages = () => {
    if (!showImagePromo) {
      return null;
    }
    return (
      <div className="semi-dark-inner-box tight centered fade-in">
        <div className="images-promo-on-page centered">
          <ImagesComponent config={{ setId: "generic" }} />
        </div>
      </div>
    );
  };

  const renderCompany = () => {
    return (
      <div
        className="centered large-top-margin semi-transparent touchable"
        onClick={handlePrivacyPolicyClick}
      >
        <strong>Datadrivers Oy</strong>
        <br />
        Hallituskatu 2 A 7
        <br />
        95400 Tornio
      </div>
    );
  };

  const renderActivity = () => {
    return (
      <div className="centered semi-dark-inner-box tight fade-in delay top-margin-small">
        <ActivityCount type="large" />
      </div>
    );
  };

  const renderActivity2 = () => {
    return (
      <div className="fade-in delay medium-top-margin pull-right phone-centered">
        <ActivityCount type="large" className="phone-centered" />
      </div>
    );
  };

  const renderMonolithInfo = (productsPage) => {
    return (
      <div id="shop-page" className="page-container max-width-container">
        <h1>
          {productsPage.title}
        </h1>
        <div className="hide-sm-and-gt">{renderChatBubblesMobile()}</div>
        {renderActivity()}
        {renderInfo(productsPage.description)}
        {renderProducts(productsPage.products)}
        <OtherProductCategories />
        {renderInfo("webshop_money_back", true)}
        {renderPaymentBanner()}
        {renderPromoImages()}
        <div className="hide-xs">{renderChatBubbles()}</div>
        {renderContactForm()}
        {renderCompany()}
      </div>
    );
  };

  const renderSplittedInfo = (productsPage) => {
    return (
      <div id="shop-page" className="page-container max-width-container">
        <div className="vertical-middle on-sides phone-column">
          <h1 className="phone-centered">
            {productsPage.title}
          </h1>
          {renderActivity2()}
        </div>
        {renderInfo(productsPage.description)}
        {renderProducts(productsPage.products)}
        <OtherProductCategories />
        {renderInfo("webshop_money_back", true)}
        <div className="vertical-middle phone-column">
          {renderInfo("webshop_info_b", true)}
          {renderPromoImages()}
        </div>
        {renderPaymentBanner()}
        <div className="vertical-middle even phone-column">
          {renderContactForm()}
          {renderCompany()}
        </div>
      </div>
    );
  };

  if (productsPageQuery.isPending) {
    return (
      <div className="medium-top-margin medium-bottom-margin">
        <Spinner />
      </div>
    );
  }

  if (productsPageQuery.isError) {
    return (
      <div className="medium-top-margin medium-bottom-margin">
        <Alert bsstyle="danger">
          <Translated translationKey={productsPageQuery.error.message} />
        </Alert>
      </div>
    );
  }

  // TODO: Use same rendering for all languages.
  if (lang !== "fi") {
    return renderMonolithInfo(productsPageQuery.data.productsPage);
  }

  return renderSplittedInfo(productsPageQuery.data.productsPage);
};

export default ProductsPage;
